/**
 * Phase 2a-4 + 2c-2 — the Artifact Studio panel for a project workspace.
 *
 * Lists the project's generated artifacts (each with its AI-drafted badge and
 * gated approve/export actions), offers a "New artifact" picker that generates
 * a first draft, and opens the generated draft in the in-workspace editor
 * surface (2b-3). The whole generate → edit → approve → export loop lives here.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useAssemblePackage, useProjectAssets, useTransitionApproval } from '../../hooks/useArtifactStudio';
import { ActionGate } from '../../lib/access';
import { AiDraftedBadge } from './AiDraftedBadge';
import { ArtifactStudioEditor } from './ArtifactStudioEditor';
import { LegalReviewBadge } from './LegalReviewBadge';
import { NewArtifactDialog } from './NewArtifactDialog';
export function ArtifactStudioPanel({ projectId }) {
    const { data, isLoading } = useProjectAssets(projectId);
    const transition = useTransitionApproval(projectId);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [openDocId, setOpenDocId] = useState(null);
    const assets = data?.assets ?? [];
    const openAsset = assets.find((a)=>a.documentId === openDocId) ?? null;
    // Editor surface takes over the panel when an asset is open.
    if (openDocId && openAsset) {
        return /*#__PURE__*/ _jsx(ArtifactStudioEditor, {
            projectId: projectId,
            documentId: openDocId,
            asset: openAsset,
            onClose: ()=>setOpenDocId(null)
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.artifactStudio.panelRoot,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg font-semibold",
                        children: "Artifact Studio"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>setDialogOpen(true),
                        "data-testid": Testids.artifactStudio.newButton,
                        children: "New artifact"
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading artifacts…"
            }) : assets.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No artifacts yet. Generate a first draft to get started."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: assets.map((asset)=>/*#__PURE__*/ _jsx(AssetRow, {
                        projectId: projectId,
                        asset: asset,
                        transition: transition,
                        onOpen: (docId)=>setOpenDocId(docId)
                    }, asset.id))
            }),
            /*#__PURE__*/ _jsx(NewArtifactDialog, {
                projectId: projectId,
                open: dialogOpen,
                onOpenChange: setDialogOpen,
                onGenerated: (documentId)=>{
                    if (documentId) setOpenDocId(documentId);
                }
            })
        ]
    });
}
function AssetRow({ projectId, asset, transition, onOpen }) {
    const assemble = useAssemblePackage();
    // Mirror the editor sidecar's export (ArtifactStudioEditor.handleExport):
    // assemble a one-asset submission package and surface the result. The
    // ActionGate above keeps the button disabled until the asset is approved.
    const handleExport = ()=>{
        assemble.mutate({
            projectId,
            assetIds: [
                asset.id
            ],
            requiredArtifactTypes: asset.artifactType ? [
                asset.artifactType
            ] : []
        }, {
            onSuccess: (res)=>{
                window.alert(res.valid ? `Exported ${res.manifestStorageKeys.length} file(s).` : `Export blocked: ${res.blockedAssetIds.length} asset(s) not approved.`);
            }
        });
    };
    // objectType/objectId pin the asset to its scope node so the Phase 0.5
    // resolver can walk the scope chain (tenant → project → asset) and honor the
    // caller's role assignment. Without them the resolve sends an empty
    // target_object_type, the resolver finds no node and fail-closes (NoView) —
    // hiding this row's own approve/export buttons. (The editor sends the same
    // pair; they don't collide in the useViewProjection cache, which keys on
    // objectType/objectId — each gate simply needs its own resolve to be correct.)
    const resourceRef = {
        kind: 'artifact_studio_asset',
        id: asset.id,
        objectType: 'artifact_studio_asset',
        objectId: asset.id
    };
    const approved = asset.approvalState === 'approved';
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex items-center gap-3 rounded-md border p-3",
        "data-testid": Testids.artifactStudio.assetRow,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate font-medium",
                                children: asset.title
                            }),
                            /*#__PURE__*/ _jsx(AiDraftedBadge, {
                                state: asset.approvalState,
                                generatedBy: asset.generatedBy
                            }),
                            asset.legalReviewRequired && /*#__PURE__*/ _jsx(LegalReviewBadge, {})
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            asset.artifactType || 'artifact',
                            " · ",
                            asset.fileType.toUpperCase()
                        ]
                    })
                ]
            }),
            asset.documentId && /*#__PURE__*/ _jsx(Button, {
                variant: "outline",
                size: "sm",
                onClick: ()=>onOpen(asset.documentId),
                "data-testid": Testids.artifactStudio.openEditorButton,
                children: "Open"
            }),
            asset.approvalState === 'ai_drafted' && /*#__PURE__*/ _jsx(Button, {
                variant: "outline",
                size: "sm",
                disabled: transition.isPending,
                onClick: ()=>transition.mutate({
                        id: asset.id,
                        toState: 'in_review'
                    }),
                "data-testid": Testids.artifactStudio.submitReviewButton,
                children: "Submit for review"
            }),
            asset.approvalState === 'in_review' && /*#__PURE__*/ _jsx(ActionGate, {
                action: "approve",
                resource: resourceRef,
                children: /*#__PURE__*/ _jsx(Button, {
                    variant: "outline",
                    size: "sm",
                    onClick: ()=>transition.mutate({
                            id: asset.id,
                            toState: 'approved',
                            reason: 'approved via panel'
                        }),
                    "data-testid": Testids.artifactStudio.approveButton,
                    children: "Approve"
                })
            }),
            /*#__PURE__*/ _jsx(ActionGate, {
                action: "export",
                resource: resourceRef,
                alsoDisabled: !approved,
                children: /*#__PURE__*/ _jsx(Button, {
                    variant: "outline",
                    size: "sm",
                    onClick: handleExport,
                    "data-testid": Testids.artifactStudio.exportButton,
                    children: "Export"
                })
            })
        ]
    });
}
